import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, MessageCircle, Car, LockKeyhole } from 'lucide-react';

const Footer = () => {
  const services = [
    { name: 'Airport Transfer', href: '/services/airport-transfer' },
    { name: 'Corporate Travel', href: '/services/corporate-travel' },
    { name: 'Sports & Entertainment', href: '/services/sports-entertainment' },
    { name: 'Production & Events', href: '/services/production-events' },
    { name: 'Long Distance Travel', href: '/services/long-distance-travel' },
    { name: 'Personal Travel', href: '/services/personal-travel' },
    { name: 'Special Occasions', href: '/services/special-occasions' }
  ];

  const quickLinks = [
    { name: 'Home', href: '/' },
    { name: 'Services', href: '/services' },
    { name: 'Fleet', href: '/fleet' },
    { name: 'About Us', href: '/about' },
    { name: 'Contact', href: '/contact' },
    { name: 'Book Now', href: '/booking' }
  ];

  // Surfaced separately at the very bottom — for drivers signing in to their PWA
  const driverPortalHref = '/driver/login';

  return (
    <footer className="bg-gray-50 text-gray-900 border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-6">
            <img
              src="https://customer-assets.emergentagent.com/job_luxury-ride-9/artifacts/bzsgrjri_Black%4010x.png"
              alt="Obsidian Chauffeurs"
              className="h-60 w-auto -ml-4"
            />
            <p className="text-gray-600 text-sm leading-relaxed">
              The Ultimate Travel Experience. Premium chauffeur services based in Cheshire, covering nationwide.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-gray-900 font-semibold mb-4 tracking-wide">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-600 hover:text-black text-sm transition-colors duration-300"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-gray-900 font-semibold mb-4 tracking-wide">Our Services</h3>
            <ul className="space-y-3">
              {services.slice(0, 6).map((service) => (
                <li key={service.name}>
                  <Link
                    to={service.href}
                    className="text-gray-600 hover:text-black text-sm transition-colors duration-300"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-gray-900 font-semibold mb-4 tracking-wide">Contact Us</h3>
            <ul className="space-y-4">
              <li>
                <a
                  href="tel:03333357733"
                  className="flex items-start space-x-3 text-gray-600 hover:text-black text-sm transition-colors duration-300 group"
                >
                  <Phone className="h-5 w-5 mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform duration-300" />
                  <div>
                    <div className="font-medium text-gray-900">Call Us</div>
                    <div>0333 335 7733</div>
                  </div>
                </a>
              </li>
              <li>
                <a
                  href="https://wa.me/443333357733"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start space-x-3 text-gray-600 hover:text-black text-sm transition-colors duration-300 group"
                >
                  <MessageCircle className="h-5 w-5 mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform duration-300" />
                  <div>
                    <div className="font-medium text-gray-900">WhatsApp</div>
                    <div>0333 335 7733</div>
                  </div>
                </a>
              </li>
              <li>
                <a
                  href="mailto:info@obsidianchauffeurs.co.uk"
                  className="flex items-start space-x-3 text-gray-600 hover:text-black text-sm transition-colors duration-300 group"
                >
                  <Mail className="h-5 w-5 mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform duration-300" />
                  <div>
                    <div className="font-medium text-gray-900">Email</div>
                    <div className="break-all">info@obsidianchauffeurs.co.uk</div>
                  </div>
                </a>
              </li>
              <li>
                <div className="flex items-start space-x-3 text-gray-600 text-sm">
                  <MapPin className="h-5 w-5 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-gray-900">Location</div>
                    <div>Bramhall, Cheshire</div>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Obsidian Chauffeurs. All rights reserved.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-gray-500">
              <Link to="/privacy" className="hover:text-black transition-colors duration-300">
                Privacy Policy
              </Link>
              <Link to="/terms" className="hover:text-black transition-colors duration-300">
                Terms of Service
              </Link>
              <span className="hidden md:inline text-gray-300">·</span>
              <Link
                to={driverPortalHref}
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-600 hover:text-black transition-colors duration-300"
                data-testid="footer-driver-portal-link"
              >
                <Car className="h-3.5 w-3.5" />
                Driver Portal
              </Link>
              <span className="hidden md:inline text-gray-300">·</span>
              <Link
                to="/admin/login"
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-600 hover:text-black transition-colors duration-300"
                data-testid="footer-admin-login-link"
              >
                <LockKeyhole className="h-3.5 w-3.5" />
                Management Console
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
